<template>
  <div class="event-team-manager">
    <div
      v-if="!hasMembers"
      class="alert alert-info"
    >
      Team has no members yet
    </div>
    <div
      v-if="hasMembers"
      class="table-responsive border mb-3"
    >
      <table class="table table-striped table-bordered table-hover table-sm mb-0 resources-table team-members">
        <thead>
          <tr>
            <th class="tight">Coach</th>
            <th>Last name</th>
            <th>First name</th>
            <th>Email</th>
            <th>Function</th>
            <th class="table-actions" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="member in visibleMembers"
            :key="member.id ?? member.uid"
          >
            <td class="tight coach">
              <input
                v-model="checkedCoaches"
                :value="member.candidate.id"
                type="checkbox"
              >
            </td>
            <td>{{ member.candidate.lastNameUppercase }}</td>
            <td>{{ member.candidate.firstName }}</td>
            <td>{{ member.candidate.email }}</td>
            <td>{{ member.candidate.functionName }}</td>
            <td class="table-actions">
              <button
                class="btn btn-sm btn-danger"
                @click.prevent="removeFromTeam(member)"
              >
                Remove from team
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <input
      v-model="coachId"
      type="hidden"
      name="event_team[coach_candidate_id]"
    >
    <template
      v-for="member in members"
      :key="member.uid"
    >
      <input
        v-model="member.id"
        type="hidden"
        name="event_team[members_attributes][][id]"
      >
      <input
        v-model="member.rm"
        type="hidden"
        name="event_team[members_attributes][][_destroy]"
      >
      <input
        v-model="member.candidate.id"
        type="hidden"
        name="event_team[members_attributes][][candidate_id]"
      >
    </template>
    <div class="row align-items-center">
      <div class="col">
        <a
          href="#"
          class="btn btn-sm btn-success"
          @click.prevent="addNewCandidate"
        >
          Add new member
        </a>
      </div>
    </div>
    <teleport
      to="#vue-portal-target"
      append
    >
      <candidate-modal
        modal-title="Add candidate"
        :search-url="searchCandidatesUrl"
        :simple-add-from-search="true"
        :show-search="candidateModalShowSearch"
        ref="candidateModal"
        @success="newCandidateModalSuccess"
        @add-candidate-to-event="onAddCandidateToEvent"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch, type PropType } from "vue"
import CandidateModal from "./candidate-modal.vue"
import { isNonEmptyString, isPresent } from "../js-utils"

interface TeamCandidate {
  id: number;
  lastNameUppercase?: string;
  firstName?: string;
  email: string;
  functionName?: string;
  isCoach?: boolean;
  last_name_uppercase?: string;
  first_name?: string;
  function_name?: string;
}

interface TeamMember {
  uid: number;
  id: number | null;
  rm: boolean;
  isCoach: boolean;
  candidate: TeamCandidate;
}

interface SearchCandidate {
  id: number;
  attributes: any;
}

const props = defineProps({
  initialMembers: { default: null, type: Array as PropType<TeamMember[] | null> },
  initialCoachId: { default: null, type: Number as PropType<number | null> },
  newCandidateUrl: { default: null, type: String },
  searchCandidatesUrl: { default: null, type: String },
})

const candidateModal = useTemplateRef<InstanceType<typeof CandidateModal>>("candidateModal")

const members = ref<TeamMember[]>([])
const visibleMembers = ref<TeamMember[]>([])
const coachId = ref<number | null>(null)

const hasMembers = computed((): boolean => {
  return members.value && members.value.length > 0
})

const checkedCoaches = computed({
  get: (): number[] => {
    return coachId.value ? [coachId.value] : []
  },
  set: (newValue: number[]): void => {
    for (const cId of newValue) {
      if (cId !== coachId.value) {
        coachId.value = cId
        return
      }
    }
    coachId.value = null
  }
})

const candidateModalShowSearch = computed((): boolean => {
  return isNonEmptyString(props.searchCandidatesUrl)
})

const updateVisibleMembers = (): void => {
  const newMembers = []
  for (const member of members.value) {
    if (!member.rm) {
      newMembers.push(member)
    }
  }
  visibleMembers.value = newMembers
}

watch(members, () => {
  updateVisibleMembers()
}, { deep: true })

onMounted(() => {
  if (props.initialMembers && props.initialMembers.length) {
    const newMembers = []
    for (const member of props.initialMembers) {
      newMembers.push(JSON.parse(JSON.stringify(member)))
    }
    members.value = newMembers
    coachId.value = props.initialCoachId
    updateVisibleMembers()
  }
})

const addNewCandidate = (): void => {
  if (isNonEmptyString(props.newCandidateUrl)) {
    candidateModal.value?.showModal(props.newCandidateUrl, null)
  }
}

const newCandidateModalSuccess = (newCandidate: TeamCandidate): void => {
  candidateModal.value?.hideModal()
  if (newCandidate) {
    members.value.push({
      uid: new Date().getTime(),
      id: null,
      rm: false,
      isCoach: false,
      candidate: newCandidate
    })
  }
}

const onAddCandidateToEvent = (candidate: SearchCandidate): void => {
  const member = JSON.parse(JSON.stringify(candidate.attributes))
  member.id = candidate.id
  member.lastNameUppercase = member.last_name_uppercase
  member.firstName = member.first_name
  member.functionName = member.function_name
  addToTeam(member)
  candidateModal.value?.hideModal()
}

const addToTeam = (candidate: TeamCandidate): void => {
  const idx = members.value.findIndex((member) => {
    return member.candidate.email === candidate.email
  })

  if (idx !== -1) {
    if (members.value[idx].rm) {
      members.value[idx].rm = false
      updateVisibleMembers()
      candidateModal.value?.hideModal()
    } else {
      alert('Member already exists in team!')
    }
  } else {
    members.value.push({
      uid: new Date().getTime(),
      id: null,
      rm: false,
      isCoach: false,
      candidate: candidate
    })
    candidateModal.value?.hideModal()
  }
}

const removeFromTeam = (member: TeamMember): void => {
  if (!confirm('Are you sure?')) {
    return
  }

  for (let i = 0; i < members.value.length; i++) {
    if (members.value[i].uid === member.uid) {
      if (coachId.value === members.value[i].candidate.id) {
        coachId.value = null
      }
      if (members.value[i].id) {
        members.value[i].rm = true
      } else {
        members.value.splice(i, 1)
      }
      break
    }
  }
  updateVisibleMembers()
}
</script>
